import React, { useState, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { InlineTextButton } from '../../components';

import css from './WorkExperienceWorkedWithContainer.css';

const INITIAL_DISPLAY_COUNT = 3;
const TEXT_PREVIEW_LENGTH = 600;
const HTML_ENTITY_MAX_LENGTH = 10;
const TAB_TYPES = {
  WORK_EXPERIENCE: 'workExperience',
  WORKED_WITH: 'workedWith',
};

const getSafeTextSlice = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  const textOnly = text.replace(/<[^>]*>/g, '');
  if (textOnly.length <= maxLength) return text;

  let sliced = text.slice(0, maxLength);

  const lastAmpersand = sliced.lastIndexOf('&');
  const lastSemicolon = sliced.lastIndexOf(';');

  if (lastAmpersand > lastSemicolon) {
    const nextSemicolon = text.indexOf(';', maxLength);
    if (nextSemicolon !== -1 && nextSemicolon - lastAmpersand < HTML_ENTITY_MAX_LENGTH) {
      sliced = text.slice(0, lastAmpersand);
    }
  }

  const lastOpenTag = sliced.lastIndexOf('<');
  const lastCloseTag = sliced.lastIndexOf('>');

  if (lastOpenTag > lastCloseTag) {
    sliced = sliced.slice(0, lastOpenTag);
  }

  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > sliced.length * 0.8) {
    sliced = sliced.slice(0, lastSpace);
  }

  return sliced;
};

// Sorting function for work experience items
const sortWorkExperienceItems = items => {
  if (!items?.length) return [];

  return items.slice().sort((a, b) => {
    // Handle ongoing jobs (jobContinues === true)
    if (a.jobContinues && b.jobContinues) {
      // Sort ongoing jobs by startYear (descending order)
      if (a.startYear !== b.startYear) {
        return b.startYear - a.startYear;
      }
      // If startYear is the same, compare by startMonth (descending order)
      return b.startMonth - a.startMonth;
    }

    // If only one job is ongoing, it should appear first
    if (a.jobContinues) return -1;
    if (b.jobContinues) return 1;

    // For completed jobs, compare by endYear first (descending order)
    if (a.endYear !== b.endYear) {
      return b.endYear - a.endYear;
    }

    // If endYear is the same, compare by endMonth (descending order)
    return b.endMonth - a.endMonth;
  });
};

// Custom hook for managing tab state and data
const useWorkExperienceTabs = (workExperience, workedWith) => {
  // Extract lengths to separate variables for static checking
  const workExperienceLength = workExperience?.length;
  const workedWithLength = workedWith?.length;

  // Determine which tab has more items to set as default
  const defaultTab = useMemo(() => {
    return (workExperienceLength || 0) >= (workedWithLength || 0)
      ? TAB_TYPES.WORK_EXPERIENCE
      : TAB_TYPES.WORKED_WITH;
  }, [workExperienceLength, workedWithLength]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showAllWorkExperience, setShowAllWorkExperience] = useState(false);
  const [showAllWorkedWith, setShowAllWorkedWith] = useState(false);

  // Memoized sorted data
  const sortedWorkExperience = useMemo(() => sortWorkExperienceItems(workExperience), [
    workExperience,
  ]);
  const sortedWorkedWith = useMemo(() => sortWorkExperienceItems(workedWith), [workedWith]);

  // Tab change handler with state reset
  const handleTabChange = useCallback(tab => {
    setActiveTab(tab);
    setShowAllWorkExperience(false);
    setShowAllWorkedWith(false);
  }, []);

  // Toggle handlers
  const toggleShowAllWorkExperience = useCallback(() => {
    setShowAllWorkExperience(true);
  }, []);

  const toggleShowAllWorkedWith = useCallback(() => {
    setShowAllWorkedWith(true);
  }, []);

  return {
    activeTab,
    showAllWorkExperience,
    showAllWorkedWith,
    sortedWorkExperience,
    sortedWorkedWith,
    handleTabChange,
    toggleShowAllWorkExperience,
    toggleShowAllWorkedWith,
  };
};

// Component for rendering individual work experience items
const WorkExperienceItem = React.memo(({ item, index }) => {
  const [showFullText, setShowFullText] = useState(false);
  const description = item.description || '';

  const { isLong, displayedText } = useMemo(() => {
    const long = description.length > TEXT_PREVIEW_LENGTH;
    const display =
      showFullText || !long
        ? description
        : `${getSafeTextSlice(description, TEXT_PREVIEW_LENGTH)}...`;
    return { isLong: long, displayedText: display };
  }, [description, showFullText]);

  const toggleText = useCallback(() => setShowFullText(prev => !prev), []);

  return (
    <div
      key={`${item.task}-${item.customerName}-${item.startMonth}-${item.startYear}-${index}`}
      className={css.workExperienceContainer}
    >
      <div className={css.workExperience}>
        <div className={css.headingContainer}>
          <div className={css.customerNameContainer}>
            <div className={css.customerName}>{item.customerName}</div>
          </div>
          <div className={css.durationContainer}>
            <div className={css.task}>{item.task}</div>
            <div className={css.durationText}>
              {item.startMonth}/{item.startYear}
              {item.jobContinues ? (
                <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
              ) : (
                ` - ${item.endMonth}/${item.endYear}`
              )}
            </div>
          </div>
        </div>
        <div className={css.text} dangerouslySetInnerHTML={{ __html: displayedText }} />
        {isLong && (
          <div className={css.toggleShowMore} onClick={toggleText}>
            {showFullText ? (
              <FormattedMessage id="WorkExperienceWorkedWithContainer.showLessText" />
            ) : (
              <FormattedMessage id="WorkExperienceWorkedWithContainer.showMoreText" />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

WorkExperienceItem.displayName = 'WorkExperienceItem';

// Component for rendering show more button
const ShowMoreButton = React.memo(({ onClick, totalCount }) => (
  <InlineTextButton className={css.toggleButton} onClick={onClick}>
    <FormattedMessage id="WorkExperienceWorkedWithContainer.showMore" />
    <div className={css.showAllCount}>&nbsp;({totalCount})</div>
  </InlineTextButton>
));

ShowMoreButton.displayName = 'ShowMoreButton';

const WorkExperienceWorkedWithContainer = props => {
  const { workExperience, workedWith } = props;

  const {
    activeTab,
    showAllWorkExperience,
    showAllWorkedWith,
    sortedWorkExperience,
    sortedWorkedWith,
    handleTabChange,
    toggleShowAllWorkExperience,
    toggleShowAllWorkedWith,
  } = useWorkExperienceTabs(workExperience, workedWith);

  // Memoized data to display for each tab
  const workExperienceToShow = useMemo(() => {
    return showAllWorkExperience
      ? sortedWorkExperience
      : sortedWorkExperience?.slice(0, INITIAL_DISPLAY_COUNT);
  }, [sortedWorkExperience, showAllWorkExperience]);

  const workedWithToShow = useMemo(() => {
    return showAllWorkedWith ? sortedWorkedWith : sortedWorkedWith?.slice(0, INITIAL_DISPLAY_COUNT);
  }, [sortedWorkedWith, showAllWorkedWith]);

  // Memoized content renderer
  const renderContent = useCallback(items => {
    if (!items?.length) return null;

    return items.map((item, index) => (
      <WorkExperienceItem
        key={`${item.task}-${item.customerName}-${item.startMonth}-${item.startYear}-${index}`}
        item={item}
        index={index}
      />
    ));
  }, []);

  // Current tab data
  const currentTabData =
    activeTab === TAB_TYPES.WORK_EXPERIENCE ? workExperienceToShow : workedWithToShow;
  const currentSortedData =
    activeTab === TAB_TYPES.WORK_EXPERIENCE ? sortedWorkExperience : sortedWorkedWith;
  const currentShowAll =
    activeTab === TAB_TYPES.WORK_EXPERIENCE ? showAllWorkExperience : showAllWorkedWith;
  const currentToggleHandler =
    activeTab === TAB_TYPES.WORK_EXPERIENCE ? toggleShowAllWorkExperience : toggleShowAllWorkedWith;

  return (
    <div id="workExperience" className={css.container}>
      <div className={css.header}>
        <FormattedMessage id="WorkExperienceWorkedWithContainer.workExperienceWorkedWith" />
      </div>

      <div className={css.tabs}>
        <InlineTextButton
          className={activeTab === TAB_TYPES.WORK_EXPERIENCE ? css.activeTab : css.inactiveTab}
          onClick={() => handleTabChange(TAB_TYPES.WORK_EXPERIENCE)}
        >
          <FormattedMessage id="WorkExperienceWorkedWithContainer.workExperienceTab" />
        </InlineTextButton>
        <InlineTextButton
          className={activeTab === TAB_TYPES.WORKED_WITH ? css.activeTab : css.inactiveTab}
          onClick={() => handleTabChange(TAB_TYPES.WORKED_WITH)}
        >
          <FormattedMessage id="WorkExperienceWorkedWithContainer.workedWithTab" />
        </InlineTextButton>
      </div>

      <div className={css.content}>
        {renderContent(currentTabData)}

        {currentSortedData?.length > INITIAL_DISPLAY_COUNT && !currentShowAll && (
          <ShowMoreButton onClick={currentToggleHandler} totalCount={currentSortedData.length} />
        )}
      </div>
    </div>
  );
};

export default WorkExperienceWorkedWithContainer;
